import { useMutation } from '@tanstack/react-query';
import { resultsApi } from '../api/results.api';
import type { MedicalResult, ResultDocument } from '../types/result.types';

interface OpenArgs {
  patientFhirId: string;
  result: MedicalResult;
  doc: ResultDocument;
}

interface OpenResult {
  /** Blob object URL of the PDF (caller opens it in a tab / triggers a save). */
  objectUrl: string;
  /** Suggested filename for the download fallback. */
  name: string;
}

function suggestName(result: MedicalResult, documentName: string | null): string {
  const base = documentName || result.consDesc || result.typeDesc || 'document';
  return base.toLowerCase().endsWith('.pdf') ? base : `${base}.pdf`;
}

/**
 * Resolves + fetches a result document and returns a blob URL for it.
 *
 * Mirrors the mobile app's logic: `GetDocument` → download the bytes → present
 * the PDF (the app opens an in-app viewer; on web the caller opens the blob URL
 * in a new tab, with a download fallback if the popup is blocked).
 */
export function useOpenDocument() {
  return useMutation<OpenResult, Error, OpenArgs>({
    mutationKey: ['results', 'open-document'],
    mutationFn: async ({ patientFhirId, result, doc }) => {
      const info = await resultsApi.getDocument(patientFhirId, result.consId, doc);
      if (!info.canDownload || !info.downloadUrl) {
        throw new Error('document_unavailable');
      }
      const blob = await resultsApi.fetchDocumentBlob(info.downloadUrl, info.contentType);
      return { objectUrl: URL.createObjectURL(blob), name: suggestName(result, info.documentName) };
    },
  });
}
